// src/pages/ResultsPage.js
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { trackEvent } from "../utils/analytics/track";

import ResultsModeSlider from "../components/results/ResultsModeSlider";

import RespiratoryRate from "../components/icons/RespiratoryRate";
import SpirometryResults from "../components/results/resultsTabs/SpirometryResults";
import CardiopulmonaryResults from "../components/results/resultsTabs/CardiopulmonaryResults";
import ManualAudioResults from "../components/results/resultsTabs/ManualAudioResults";
import PostExertionResults from "../components/results/resultsTabs/PostExertionResults";
import Spinner from "../components/Spinner";
import { RECORDING_MODES } from "../utils/recordingModes";
import Pulmonology from "../components/icons/Pulmonology";
import VoiceSelectionIcon from "../components/icons/VoiceSelectionIcon";
import StackedLineChartIcon from "../components/icons/StackedLineChartIcon";

function ResultsPage({
  db,
  user,
  isPermitted,
  loadingModes,
  firstRunByVariant,
}) {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMode = searchParams.get("tab") || "";

  useEffect(() => {
    trackEvent("results_view", { lang: i18n.language || null });
  }, [i18n.language]);

  const items = useMemo(() => {
    const nextItems = [];

    if (isPermitted?.(RECORDING_MODES.SPIROMETRY)) {
      nextItems.push({
        value: "spiro",
        icon: <Pulmonology className="w-7 h-7" />,
        label: t("results.modes.spirometry", "Spirometry"),
      });
    }

    if (isPermitted?.(RECORDING_MODES.TIDAL)) {
      nextItems.push({
        value: "cardiopulmonary",
        icon: <RespiratoryRate className="w-7 h-7" />,
        label: t("results.modes.cardiopulmonary", "Cardiopulmonary"),
      });
    }

    if (isPermitted?.(RECORDING_MODES.MAUDIO)) {
      nextItems.push({
        value: "manual-audio",
        icon: <VoiceSelectionIcon className="w-7 h-7" />,
        label: t("results.modes.manualAudio", "Manual Audio"),
      });
    }

    if (isPermitted?.(RECORDING_MODES.POSTEXERTION)) {
      nextItems.push({
        value: "post-exertion",
        icon: <StackedLineChartIcon className="w-7 h-7" />,
        label: t("results.modes.postExertion", "Exertion"),
      });
    }

    return nextItems;
  }, [isPermitted, t]);

  const activeMode = items.some((item) => item.value === requestedMode)
    ? requestedMode
    : items[0]?.value;

  useEffect(() => {
    if (!activeMode || requestedMode === activeMode) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", activeMode);
    setSearchParams(nextParams, { replace: true });
  }, [activeMode, requestedMode, searchParams, setSearchParams]);

  const handleModeChange = useCallback(
    (nextMode) => {
      if (!nextMode || nextMode === requestedMode) return;

      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("tab", nextMode);
      setSearchParams(nextParams, { replace: true });
    },
    [requestedMode, searchParams, setSearchParams],
  );

  useEffect(() => {
    if (!activeMode) return;
    trackEvent("results_mode_change", { mode: activeMode });
  }, [activeMode]);

  if (loadingModes) {
    return (
      <div className="min-h-64 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="muted-text p-4">
        {t("results.noneAvailable", "No results available.")}
      </div>
    );
  }

  const title = (() => {
    if (activeMode === "cardiopulmonary") {
      return t("results.cardiopulmonary.title", "Cardiopulmonary Scan Results");
    }

    if (activeMode === "manual-audio") {
      return t("results.manualAudio.title", "Manual Audio Recording Results");
    }

    if (activeMode === "post-exertion") {
      return t(
        "results.postExertion.title",
        "Exertion Recovery Recording Results",
      );
    }

    return t("results.spiro.title", "Spirometry Scan Results");
  })();

  return (
    <div className="w-full max-w-lg h-full min-h-0 p-4 flex flex-col gap-4 overflow-hidden">
      {/* IMPORTANT: flex-1 + min-h-0 so children can overflow */}
      <section className="w-full flex-1 min-h-0 flex flex-col items-center text-center">
        <h2 className="subtitle-text">{title}</h2>

        {/* IMPORTANT: also flex-1 + min-h-0 */}
        <div className="w-full flex-1 min-h-0">
          {activeMode === "spiro" ? (
            <SpirometryResults
              db={db}
              uid={user?.uid}
              firstRunByVariant={firstRunByVariant}
            />
          ) : activeMode === "cardiopulmonary" ? (
            <CardiopulmonaryResults
              db={db}
              uid={user?.uid}
              firstRunByVariant={firstRunByVariant}
            />
          ) : activeMode === "manual-audio" ? (
            <ManualAudioResults
              db={db}
              uid={user?.uid}
              firstRunByVariant={firstRunByVariant}
            />
          ) : activeMode === "post-exertion" ? (
            <PostExertionResults
              db={db}
              uid={user?.uid}
              firstRunByVariant={firstRunByVariant}
            />
          ) : null}
        </div>
      </section>

      {/* Top selector */}
      {items.length > 1 ? (
        <ResultsModeSlider
          value={activeMode}
          onChange={handleModeChange}
          items={items}
        />
      ) : null}
    </div>
  );
}

export default memo(ResultsPage);
